// src/lib/validation/validation.ts
import { pluralize, singularize } from "inflection";
var TWO_DECIMAL_PLACES_WITH_TRAILING_DOT = /^[0-9]*(\.)?([0-9]{1,2})?$/;
var FOUR_DECIMAL_PLACES_WITH_TRAILING_DOT = /^[0-9]*(\.)?([0-9]{1,4})?$/;
var TEN_DECIMAL_PLACES_WITH_TRAILING_DOT = /^[0-9]*(\.)?([0-9]{1,10})?$/;
var POSITIVE_INTEGER_PATTERN = /^[0-9]+$/;
var INTEGER_PATTERN = /^[-]?[0-9]+$/;
var EMAIL_PATTERN = (
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);
var URL_PATTERN = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;
var THREE_DIGIT_NUMBER = /^[0-9]{3}$/;
var FIVE_DIGIT_NUMBER = /^[0-9]{5}$/;
var SIX_DIGIT_NUMBER = /^[0-9]{6}$/;
var EIGHT_DIGIT_NUMBER = /^[0-9]{8}$/;
var NINE_DIGIT_NUMBER = /^[0-9]{9}$/;
var SWIFT_CODE = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
var validateDecimals = (value, decimalPlaces) => {
  if (typeof value !== "string") {
    return false;
  }
  switch (decimalPlaces) {
    case "2":
      return TWO_DECIMAL_PLACES_WITH_TRAILING_DOT.test(value);
    case "4":
      return FOUR_DECIMAL_PLACES_WITH_TRAILING_DOT.test(value);
    case "10":
      return TEN_DECIMAL_PLACES_WITH_TRAILING_DOT.test(value);
  }
};
var composeValidators = (...validators) => (value, allValues) => validators.reduce(
  (error, validator) => error || validator(value, allValues),
  void 0
);
var required = (value) => {
  if (Array.isArray(value) && value.length === 0) {
    return "This field is required";
  }
  return value ? void 0 : "This field is required";
};
var requiredAllowZero = (value) => {
  if (Array.isArray(value) && value.length === 0) {
    return "This field is required";
  }
  return value || value === 0 ? void 0 : "This field is required";
};
var skip = () => void 0;
var requiredIfFieldPopulated = (otherField, message) => (value, allValues) => {
  return allValues[otherField] ? value ? void 0 : message : void 0;
};
var dateMustBeInFuture = (value) => {
  const today = /* @__PURE__ */ new Date();
  const startOfToday = new Date(
    Date.parse(
      `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()} 00:00:00`
    )
  );
  return value < startOfToday ? "Date must be in the future" : void 0;
};
var dateMustBeAfter = (otherField, message) => (value, allValues) => {
  return !allValues[otherField] || value > allValues[otherField] ? void 0 : message;
};
var dateMustBeBefore = (otherField, message) => (value, allValues) => {
  const values = allValues;
  return !values[otherField] || value < values[otherField] ? void 0 : message;
};
var greaterThan = (tooLow, message) => (value) => {
  return value > tooLow ? void 0 : message ?? `Please enter a number greater than ${tooLow}`;
};
var greaterThanEqualTo = (tooLow, message) => (value) => {
  return value >= tooLow ? void 0 : message ?? `Please enter a number greater than or equal to ${tooLow}`;
};
var lessThan = (tooHigh, message) => (value) => {
  return value < tooHigh ? void 0 : message ?? `Please enter a number less than ${tooHigh}`;
};
var lessThanEqualTo = (tooHigh, message) => (value) => {
  return value <= tooHigh ? void 0 : message ?? `Please enter a number less than or equal to ${tooHigh}`;
};
var percentage = (value) => {
  return value >= 0 && value <= 100 ? void 0 : `Please enter a number between 0 and 100`;
};
var decimal = (decimalPlaces) => (value) => {
  if (typeof value === "undefined") {
    return void 0;
  }
  return validateDecimals(value, decimalPlaces) ? void 0 : `Please enter a number with up to ${decimalPlaces} decimal places`;
};
var isStringInteger = (value) => {
  return typeof value === "string" && INTEGER_PATTERN.test(value) ? void 0 : "Please enter a number";
};
var isInteger = (value) => {
  return typeof value === "string" && INTEGER_PATTERN.test(value) ? void 0 : "Please enter an integer";
};
var isPositiveInteger = (value) => {
  return typeof value === "string" && POSITIVE_INTEGER_PATTERN.test(value) ? void 0 : "Please enter a positive integer";
};
var isEmail = (value) => {
  if (value === void 0) {
    return void 0;
  }
  return typeof value === "string" && EMAIL_PATTERN.test(value) ? void 0 : "Please enter a valid email address";
};
var isUrl = (value) => {
  if (value === void 0) {
    return void 0;
  }
  return typeof value === "string" && URL_PATTERN.test(value) ? void 0 : "Please enter a valid URL";
};
var isGoogleDocsUrl = (value) => {
  if (value === void 0) {
    return void 0;
  }
  return typeof value === "string" && value.startsWith("https://docs.google.com") ? void 0 : "Please enter a valid Google Sheet URL";
};
var threeDigitNumber = (value) => {
  return typeof value === "string" && THREE_DIGIT_NUMBER.test(value) ? void 0 : "Please enter a number with 3 digits";
};
var fiveDigitNumber = (value) => {
  return typeof value === "string" && FIVE_DIGIT_NUMBER.test(value) ? void 0 : "Please enter a number with 5 digits";
};
function numberWithLengthBetween(minLength, maxLength) {
  return (value) => {
    return typeof value === "string" && INTEGER_PATTERN.test(value) && value.length >= minLength && value.length <= maxLength ? void 0 : `Please enter a number with between ${minLength} and ${maxLength} digits`;
  };
}
var sixDigitNumber = (value) => {
  return typeof value === "string" && SIX_DIGIT_NUMBER.test(value) ? void 0 : "Please enter a number with 6 digits";
};
var eightDigitNumber = (value) => {
  return typeof value === "string" && EIGHT_DIGIT_NUMBER.test(value) ? void 0 : "Please enter a number with 8 digits";
};
var nineDigitNumber = (value) => {
  return typeof value === "string" && NINE_DIGIT_NUMBER.test(value) ? void 0 : "Please enter a number with 9 digits";
};
var swiftCode = (value) => {
  return typeof value === "string" && SWIFT_CODE.test(value) ? void 0 : "Please enter a valid Swift Code";
};
var atLeast = (count, name) => (value) => {
  return Array.isArray(value) && value.length >= count ? void 0 : `Please enter at least ${count} ${name}`;
};
var notInListCaseInsensitive = (list) => (value) => {
  if (value === void 0) {
    return void 0;
  }
  return typeof value === "string" && !list.map((listItem) => listItem.toLowerCase()).includes(value.toLowerCase()) ? void 0 : `Must be unique`;
};
var isJson = (value) => {
  if (value === void 0) {
    return void 0;
  }
  try {
    JSON.parse(value);
  } catch (_e) {
    return "Please enter valid JSON";
  }
};
var isValidDate = (date) => {
  if (!(date instanceof Date)) {
    return false;
  }
  try {
    if (date.toString() === "Invalid Date") {
      return false;
    }
  } catch (_e) {
    return false;
  }
  return true;
};
var validDate = (date) => {
  return !isValidDate(date) ? "Date is invalid" : void 0;
};
var isUkBankHoliday = (holidayDatesMap) => ({ dateString }) => {
  if (holidayDatesMap[dateString]) {
    return `${dateString} is a holiday and not selectable`;
  }
};
var isWeekend = ({ dateObject, dateString }) => {
  const day = dateObject.getDay();
  if (day === 0 || day === 6) {
    return `${dateString} is a weekend day and not selectable`;
  }
};
var isEmailsList = ({ permitEmpty }) => (values) => {
  if (values === void 0 || !Array.isArray(values) || values.length === 0) {
    return "Please enter at least one email address";
  }
  const filteredValues = values.filter((value) => !(permitEmpty && !value));
  if (new Set(filteredValues).size !== filteredValues.length) {
    return "Please remove any duplicate email addresses";
  }
  const emailCount = filteredValues.reduce((count, value) => {
    if (typeof value === "string" && EMAIL_PATTERN.test(value)) {
      return count + 1;
    } else {
      return count;
    }
  }, 0);
  if (!emailCount) {
    return "Please enter at least one email address";
  }
  const invalid = filteredValues.filter(
    (value) => typeof value === "string" && !EMAIL_PATTERN.test(value)
  );
  if (invalid.length === 1) {
    return `${invalid[0]} is not a valid email address`;
  } else if (invalid.length > 1) {
    return `${invalid.join(", ")} are not valid email addresses`;
  }
};
var validateParameter = (type) => {
  switch (type) {
    case "INTEGER":
      return isInteger;
    case "POSITIVE_INTEGER":
      return composeValidators(isPositiveInteger, greaterThanEqualTo(0));
    case "NUMBER":
      return isNumber;
    case "POSITIVE_NUMBER":
      return isPositiveNumber;
  }
};
var forAllInArray = ({
  validator,
  valueDescription,
  permitEmptyValue = false,
  permitEmptyArray = false
}) => (values) => {
  if (values === void 0 || !Array.isArray(values) || !permitEmptyArray && values.length === 0) {
    return `Please enter at least one ${singularize(valueDescription)}`;
  }
  const filteredValues = values.filter((value) => !(permitEmptyValue && !value));
  if (new Set(filteredValues).size !== filteredValues.length) {
    return `Please remove any duplicate ${pluralize(valueDescription)}`;
  }
  const matchCount = filteredValues.reduce((count, value) => {
    if (validator(value) === void 0) {
      return count + 1;
    } else {
      return count;
    }
  }, 0);
  if (!permitEmptyArray && !matchCount) {
    return `Please enter at least one ${singularize(valueDescription)}`;
  }
  const invalid = filteredValues.filter(
    (value) => validator(value) !== void 0
  );
  if (invalid.length === 1) {
    return `${invalid[0]} is not a valid ${singularize(valueDescription)}`;
  } else if (invalid.length > 1) {
    return `${invalid.join(", ")} are not valid ${pluralize(
      valueDescription
    )}`;
  }
};
var isNumber = (value) => {
  if (typeof value === "number") {
    return void 0;
  }
  if (typeof value === "string") {
    const trimmedValue = value.trim();
    if (!isNaN(Number(trimmedValue)) && trimmedValue !== "") {
      return void 0;
    }
  }
  return "Please enter a number";
};
var isPositiveNumber = (value) => {
  const initialValidationPassed = isNumber(value) === void 0;
  const errorMessage = "Please enter a positive number";
  if (!initialValidationPassed) {
    return errorMessage;
  }
  return Number(value) > 0 ? void 0 : errorMessage;
};
export {
  EMAIL_PATTERN,
  FOUR_DECIMAL_PLACES_WITH_TRAILING_DOT,
  TEN_DECIMAL_PLACES_WITH_TRAILING_DOT,
  TWO_DECIMAL_PLACES_WITH_TRAILING_DOT,
  URL_PATTERN,
  atLeast,
  composeValidators,
  dateMustBeAfter,
  dateMustBeBefore,
  dateMustBeInFuture,
  decimal,
  eightDigitNumber,
  fiveDigitNumber,
  forAllInArray,
  greaterThan,
  greaterThanEqualTo,
  isEmail,
  isEmailsList,
  isGoogleDocsUrl,
  isInteger,
  isJson,
  isNumber,
  isPositiveInteger,
  isPositiveNumber,
  isStringInteger,
  isUkBankHoliday,
  isUrl,
  isValidDate,
  isWeekend,
  lessThan,
  lessThanEqualTo,
  nineDigitNumber,
  notInListCaseInsensitive,
  numberWithLengthBetween,
  percentage,
  required,
  requiredAllowZero,
  requiredIfFieldPopulated,
  sixDigitNumber,
  skip,
  swiftCode,
  threeDigitNumber,
  validDate,
  validateDecimals,
  validateParameter
};
//# sourceMappingURL=index.js.map